import { faThumbsUp } from "@fortawesome/free-regular-svg-icons/faThumbsUp";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";
import { faNewspaper } from "@fortawesome/free-solid-svg-icons/faNewspaper";
import { faReply } from "@fortawesome/free-solid-svg-icons/faReply";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons/faShareAlt";

import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";

type MockDiscussion = {
  sentiment: "Bullish" | "Bearish";
  title: string;
  date: string;
  body: string;
  insightTitle: string;
  insightByline: string;
  author: string;
  impressions: number;
};

const mockDiscussions: MockDiscussion[] = [
  {
    sentiment: "Bearish",
    title: "Lorem Ipsum Dolor Sit Amet (123 AB)...",
    date: "2 days ago",
    body: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. ...",
    insightTitle: "Lorem Ipsum Dolor Sit Amet, Consectetur Adipiscing...",
    insightByline:
      "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident.",
    author: "Lorem Ipsum",
    impressions: 11,
  },
  {
    sentiment: "Bullish",
    title: "Consectetur Adipiscing Elit...",
    date: "19 Jun 2026 16:09",
    body: "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. ...",
    insightTitle: "Neque Porro Quisquam Est Qui Dolorem Ipsum",
    insightByline:
      "At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias.",
    author: "Dolor Amet",
    impressions: 46,
  },
  {
    sentiment: "Bullish",
    title: "Tempor Incididunt Ut Labore (...",
    date: "19 Jun 2026 09:31",
    body: "Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.",
    insightTitle: "Ut Enim Ad Minima Veniam, Quis Nostrum",
    insightByline:
      "Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat.",
    author: "Sit Consectetur",
    impressions: 8,
  },
];

const MockSnippet = ({ discussion }: { discussion: MockDiscussion }) => (
  <div className="border border-solid border-black/[0.133] rounded-[5px] p-[15px] mb-[25px]">
    <div className="text-[14px] mb-[10px]">
      <span
        className={
          discussion.sentiment === "Bullish"
            ? "text-bullish-green font-medium"
            : "text-bearish-red font-medium"
        }
      >
        {discussion.sentiment}
      </span>
      <span className="text-intelligent-silver"> · {discussion.title} · </span>
      <span className="text-intelligent-silver">{discussion.date}</span>
    </div>

    <p className="text-[16px] text-subtle-black leading-[1.5] mb-[15px]">
      {discussion.body}
    </p>

    <div className="flex border border-solid border-[#eee] rounded-[5px] overflow-hidden mb-[15px]">
      <div className="flex flex-col items-center justify-center px-[20px] py-[25px] min-w-[180px]">
        <div className="w-[64px] h-[64px] bg-smart-teal/10 rounded-full flex items-center justify-center mb-[10px]">
          <FontAwesomeIcon
            icon={faNewspaper}
            className="text-smart-teal text-[24px]"
          />
        </div>
        <span className="font-medium text-[18px] text-subtle-black">
          Research
        </span>
      </div>
      <div className="border-l border-solid border-[#eee] flex-1 px-[25px] py-[20px]">
        <h3 className="text-[20px] font-medium text-subtle-black leading-[1.3] mb-[8px]">
          {discussion.insightTitle}
        </h3>
        <p className="text-[15px] text-intelligent-silver leading-[1.5]">
          {discussion.insightByline}
        </p>
      </div>
    </div>

    <div className="flex items-center">
      <div className="w-[40px] h-[40px] rounded-full bg-black/10 flex-shrink-0" />
      <div className="ml-[10px] flex-1">
        <div className="text-[16px] text-intelligent-silver">
          {discussion.author}
        </div>
        <div className="text-[13px] text-intelligent-silver">
          {discussion.impressions} Impressions
        </div>
      </div>
      <div className="flex text-intelligent-silver space-x-[20px] mr-[10px] text-[18px]">
        <FontAwesomeIcon icon={faThumbsUp} />
        <FontAwesomeIcon icon={faShareAlt} />
        <FontAwesomeIcon icon={faReply} />
      </div>
    </div>
  </div>
);

const DiscussionsPlaceholder = () => {
  const { toggleModal } = useSignInSignUp();

  return (
    <CustomCard className="relative overflow-hidden min-h-[calc(100vh-100px)] p-[20px]">
      {/* Blurred mock discussions in the background */}
      <div aria-hidden className="blur-[6px] select-none pointer-events-none">
        {mockDiscussions.map((discussion) => (
          <MockSnippet key={discussion.title} discussion={discussion} />
        ))}
      </div>

      {/* Glass-morphed lock overlay */}
      <div className="absolute inset-0 flex items-start justify-center bg-white/40 backdrop-blur-[2px]">
        <div className="mt-[120px] mx-[20px] max-w-[520px] text-center bg-white/80 backdrop-blur-md border border-solid border-white/60 rounded-[12px] shadow-lg px-[30px] py-[35px]">
          <div className="w-[64px] h-[64px] mx-auto mb-[20px] rounded-full bg-smart-teal/10 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faLock}
              className="text-smart-teal text-[26px]"
            />
          </div>
          <p className="text-[16px] text-subtle-black leading-[1.6] mb-[20px]">
            Your subscription enables you to engage in discussions with our
            network of independent analysts, investor relations professionals,
            and investors.
          </p>
          <a
            className="inline-block cursor-pointer bg-smart-teal hover:bg-dark-teal text-white hover:text-white font-medium rounded-[6px] px-[24px] py-[10px] no-underline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleModal("sign-up");
            }}
            rel="noreferrer"
          >
            Sign Up
          </a>
        </div>
      </div>
    </CustomCard>
  );
};

export default DiscussionsPlaceholder;
